import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { API, GeoCullDTO, PlayerCullDTO, TimeCullDTO, VerificationDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { FreetextResult } from 'src/app/services/gui/modal/modal.model';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { TableWrapperOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QueryParamService } from '../../../services/lists/query-params';
import { FilterEditComponent } from '../../datasets/filter-edit/filter-edit.component';
import { FilterListComponent } from '../../datasets/filter-list/filter-list.component';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

type CullType = 'PlayerCull' | 'GeoCull' | 'TimeCull';

@Component({
  selector: 'sb-page-verification',
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.scss'],
  standalone: false,
})
export class VerificationComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  LIFTREJECTIONALLOWED: boolean = false;
  PENDING: string = 'Pending';
  REJECTED: string = 'Rejected';
  VERIFIED: string = 'Verified';
  defaultReasonRejection: string = this.translate.instant('lang.globals.defaultReasonRejection');

  debounceTimer: ReturnType<typeof setTimeout>;
  verifications: VerificationDTO[];
  loading: boolean;
  type: string;
  queryParamsParsed: boolean;

  options: TableWrapperOptions;

  multiSelect: boolean;
  selected: VerificationDTO[];
  showSideBar: boolean;
  singleSelectedVerification: VerificationDTO;

  constructor(
    private readonly api: API,
    public readonly modalService: ModalService,
    public readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef,
    private readonly queryParamService: QueryParamService
  ) {
    this.singleSelectedVerification = null;
    this.showSideBar = false;
    this.clearSelected();
    this.verifications = [];
    this.debounceTimer = null;
    this.loading = true;
    this.type = this.PENDING;
    this.queryParamsParsed = false;
  }

  ngOnInit() {
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.queryParamsParsed = true;
        this.requestDebounced();
      });
    }
  }

  parseQueryParams() {
    return new Promise(resolve => {
      this.parseTypeQueryParams();
      resolve(true);
    });
  }

  parseTypeQueryParams() {
    const type: string = this.queryParamService.getQueryParam('type');

    if (type === this.REJECTED || type === this.VERIFIED) {
      this.type = type;
    } else {
      this.type = this.PENDING;
    }
  }

  refreshQueryParams() {
    this.refreshTypeQueryParam();
  }

  refreshTypeQueryParam() {
    this.queryParamService.setQueryParam('type', this.type);
  }

  isPending(verification: VerificationDTO): boolean {
    return !verification?.verifiedAt && !verification?.rejectedAt;
  }

  isVerified(verification: VerificationDTO): boolean {
    return verification?.rejectedAt && verification?.verifiedAt
      ? verification?.verifiedAt.value > verification?.rejectedAt.value
      : !verification?.rejectedAt;
  }

  isRejected(verification: VerificationDTO): boolean {
    return verification?.rejectedAt && verification?.verifiedAt
      ? verification?.verifiedAt.value <= verification?.rejectedAt.value
      : !verification?.verifiedAt;
  }

  showReason(verification: VerificationDTO) {
    this.modalService
      .alert(this.translate.instant('lang.verification.verificationRejectedReason'), verification.reason)
      .then(() => {
        /*empty*/
      })
      .catch(() => {
        /*empty*/
      });
  }

  approveBooking(verification: VerificationDTO) {
    verification.permitted = true;
    verification.reason = null;
    this.editVerification(verification);
  }

  rejectBooking(verification: VerificationDTO) {
    this.modalService
      .freetext({
        title: this.translate.instant('lang.globals.reason'),
        initial: this.defaultReasonRejection,
      })
      .then((e: FreetextResult<string>) => {
        if (!e.cancel) {
          const reason: string = e.value;
          verification.permitted = false;
          verification.reason = reason || this.defaultReasonRejection;
          this.editVerification(verification);
        }
      })
      .catch(e => {
        this.errorHandler.errorHandler(e);
      });
  }

  liftRejection(verification: VerificationDTO) {
    verification.permitted = true;
    verification.reason = '';
    this.editVerification(verification);
  }

  liftApproval(verification: VerificationDTO) {
    verification.permitted = false;
    verification.reason = '';
    this.editVerification(verification);
  }

  editVerification(verification: VerificationDTO) {
    this.verifications = [];
    this.api
      .ss_nm_Verification_edit(verification)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.singleSelectedVerification = null;
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }

    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    if (this.options?.pagination) {
      this.loading = true;
      let obs: Observable<VerificationDTO[]> = null;

      switch (this.type) {
        case this.PENDING:
          obs = this.api.ss_nm_Verification_pending(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.options.search),
            },
            this.options.pagination
          );
          break;
        case this.REJECTED:
          obs = this.api.ss_nm_Verification_rejected(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.options.search),
            },
            this.options.pagination
          );
          break;
        case this.VERIFIED:
          obs = this.api.ss_nm_Verification_verified(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.options.search),
            },
            this.options.pagination
          );
          break;
      }

      if (obs) {
        obs
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: data => {
              this.verifications = data;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  selectedStatus(e: string) {
    this.type = e;
    if (this.options?.pagination) {
      this.loading = true;
      this.verifications = [];
    }
    this.requestDebounced();
  }

  acceptSelected() {
    if (this.selected?.length > 0) {
      for (const verification of this.selected) {
        verification.permitted = true;
        verification.reason = null;
      }
      this.editMultipleVerification();
    } else {
      this.clearSelected();
    }
  }

  rejectSelected() {
    if (this.selected?.length > 0) {
      this.modalService
        .freetext({
          title: this.translate.instant('lang.globals.reason'),
          initial: this.defaultReasonRejection,
        })
        .then((e: FreetextResult<string>) => {
          if (!e.cancel) {
            const reason: string = e.value;
            for (const verification of this.selected) {
              verification.permitted = false;
              verification.reason = reason || this.defaultReasonRejection;
            }
            this.editMultipleVerification();
          }
        })
        .catch(e => {
          this.errorHandler.errorHandler(e);
        });
    } else {
      this.clearSelected();
    }
  }

  clearSelected() {
    this.selected = [];
    this.multiSelect = false;
  }

  selectVerification(verification: VerificationDTO) {
    const found = this.checkSelection(verification);
    if (found !== -1) {
      this.selected.splice(found, 1);
    } else {
      this.selected.push(verification);
    }
  }

  private editMultipleVerification() {
    this.api
      .ss_nm_Verification_editMultiple(this.selected)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: () => {
          this.singleSelectedVerification = null;
          this.verifications = [];
          this.clearSelected();
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  checkSelection(pVerification: VerificationDTO): number {
    for (const [index, verification] of this.selected.entries()) {
      if (
        pVerification?.id?.medium?.id === verification?.id?.medium?.id &&
        pVerification?.id?.marker?.id === verification?.id?.marker?.id
      ) {
        return index;
      }
    }
    return -1;
  }

  selectionState() {
    if (this.multiSelect) {
      this.clearSelected();
    } else {
      this.multiSelect = true;
    }
  }

  selectAll() {
    for (const verification of this.verifications) {
      if (this.checkSelection(verification) === -1) {
        this.selected.push(verification);
      }
    }
  }

  deselectAll() {
    for (const verification of this.verifications) {
      const found = this.checkSelection(verification);
      if (found !== -1) {
        this.selected.splice(found, 1);
      }
    }
  }

  get headline(): string {
    if (this.type === this.PENDING) {
      return this.translate.instant('lang.globals.plural.pending') + ' ' + this.translate.instant('lang.plural.verification');
    }

    if (this.type === this.VERIFIED) {
      return this.translate.instant('lang.globals.plural.verified') + ' ' + this.translate.instant('lang.globals.bookings');
    }

    if (this.type === this.REJECTED) {
      return this.translate.instant('lang.globals.plural.rejected') + ' ' + this.translate.instant('lang.globals.bookings');
    }
    return '';
  }

  clearEdit() {
    this.singleSelectedVerification = null;
    this.showSideBar = false;
    this.requestDebounced();
  }

  saveVerification() {
    this.showSideBar = false;
    this.editVerification(this.singleSelectedVerification);
  }

  toggleEdit(verification: VerificationDTO) {
    this.showSideBar = true;
    this.singleSelectedVerification = verification;
  }

  selectCull(type: CullType) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: FilterListComponent, data: { preselectType: type } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: GeoCullDTO | TimeCullDTO | PlayerCullDTO) => {
        if (result) {
          switch (type) {
            case 'GeoCull':
              this.singleSelectedVerification.geoCull = result as GeoCullDTO;
              break;
            case 'TimeCull':
              this.singleSelectedVerification.timeCull = result as TimeCullDTO;
              break;
            case 'PlayerCull':
              this.singleSelectedVerification.playerCull = result as PlayerCullDTO;
              break;
          }
        }
      })
      .catch(() => {});
  }

  createCull(type: CullType) {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: FilterEditComponent, data: { cullType: type, companyDisabled: true } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: GeoCullDTO | TimeCullDTO | PlayerCullDTO) => {
        if (result) {
          switch (type) {
            case 'GeoCull':
              this.singleSelectedVerification.geoCull = result as GeoCullDTO;
              break;
            case 'TimeCull':
              this.singleSelectedVerification.timeCull = result as TimeCullDTO;
              break;
            case 'PlayerCull':
              this.singleSelectedVerification.playerCull = result as PlayerCullDTO;
              break;
          }
        }
      })
      .catch(() => {});
  }

  deleteCull(type: CullType) {
    if (!type) {
      return;
    }
    switch (type) {
      case 'GeoCull':
        this.singleSelectedVerification.geoCull = null;
        break;
      case 'TimeCull':
        this.singleSelectedVerification.timeCull = null;
        break;
      case 'PlayerCull':
        this.singleSelectedVerification.playerCull = null;
        break;
    }
  }
}
